import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const skillCategories = [
  {
    title: "Frontend Compounds",
    skills: [
      { name: 'React', symbol: 'Re', number: 18, level: 95 },
      { name: 'JavaScript', symbol: 'Js', number: 16, level: 92 },
      { name: 'TypeScript', symbol: 'Ts', number: 22, level: 85 },
      { name: 'Next.js', symbol: 'Nx', number: 7, level: 88 },
      { name: 'TailwindCSS', symbol: 'Tw', number: 81, level: 90 },
      { name: 'HTML5/CSS3', symbol: 'H5', number: 1, level: 98 },
    ]
  },
  {
    title: "Backend Compounds",
    skills: [
      { name: 'Node.js', symbol: 'Nd', number: 10, level: 90 },
      { name: 'Python', symbol: 'Py', number: 15, level: 88 },
      { name: 'Django', symbol: 'Dj', number: 26, level: 85 },
      { name: 'Express', symbol: 'Ex', number: 25, level: 88 },
      { name: 'MongoDB', symbol: 'Mg', number: 12, level: 85 },
      { name: 'PostgreSQL', symbol: 'Pg', number: 46, level: 82 },
    ]
  },
  {
    title: "Lab Equipment (Tools & Cloud)",
    skills: [
      { name: 'Git', symbol: 'Gt', number: 32, level: 95 },
      { name: 'Docker', symbol: 'Dk', number: 26, level: 78 },
      { name: 'AWS', symbol: 'Aw', number: 33, level: 75 },
      { name: 'Linux', symbol: 'Lx', number: 3, level: 82 },
      { name: 'Firebase', symbol: 'Fb', number: 9, level: 80 },
      { name: 'REST APIs', symbol: 'Rs', number: 44, level: 92 },
    ]
  }
];

const SkillElement = ({ skill, index }) => {
  const getPurityColor = (level) => {
    if (level >= 90) return 'from-green-400 to-bb-green';
    if (level >= 80) return 'from-green-500 to-green-600';
    if (level >= 70) return 'from-yellow-500 to-yellow-600';
    return 'from-orange-500 to-orange-600';
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      whileHover={{
        scale: 1.05,
        boxShadow: '0 0 25px rgba(0, 200, 83, 0.4)'
      }}
      className="bg-gray-800/80 border border-green-900 rounded-lg p-4 hover:border-bb-green transition-all"
    >
      <div className="flex items-center gap-4">
        {/* Element Symbol */}
        <motion.div
          className="w-14 h-14 bg-gray-900 border-2 border-bb-green flex flex-col items-center justify-center relative"
          whileHover={{ rotate: [0, -5, 5, 0] }}
        >
          <span className="text-[8px] absolute top-0.5 left-1 text-gray-500">{skill.number}</span>
          <span className="text-xl font-bold text-bb-green-light">{skill.symbol}</span>
        </motion.div>

        {/* Skill Info */}
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-semibold">{skill.name}</span>
            <span className="text-bb-green-light text-sm">{skill.level}% Pure</span>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              className={`h-full bg-gradient-to-r ${getPurityColor(skill.level)} rounded-full`}
              initial={{ width: 0 }}
              whileInView={{ width: `${skill.level}%` }}
              transition={{ duration: 1, delay: index * 0.1 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="skills" className="py-20 px-4 relative">
      {/* Molecular Background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-4 h-4 border-2 border-green-900/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          />
        ))}
      </div>

      <div ref={ref} className="max-w-6xl mx-auto relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">My </span>
            <span className="text-bb-green-light">Compounds</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Like a master chemist, I've perfected my formulas over years of experimentation.
            Here's my periodic table of skills, each one carefully synthesized.
          </p>
          <div className="w-24 h-1 bg-bb-green mx-auto mt-4" />
        </motion.div>

        {/* Skill Categories */}
        {skillCategories.map((category, catIndex) => (
          <motion.div
            key={category.title}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: catIndex * 0.2 }}
            className="mb-12"
          >
            <h3 className="text-2xl font-bold text-bb-green-light mb-6 flex items-center gap-3">
              <span className="w-8 h-8 bg-bb-green/20 border border-bb-green flex items-center justify-center text-sm">
                {catIndex + 1}
              </span>
              {category.title}
            </h3>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.skills.map((skill, index) => (
                <SkillElement key={skill.name} skill={skill} index={index} />
              ))}
            </div>
          </motion.div>
        ))}

        {/* Purity Meter */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-gray-800/50 border border-green-900 rounded-lg p-6"
        >
          <h4 className="text-xl font-bold text-white mb-4 text-center">Overall Code Purity</h4>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            {/* Responsive Circle */}
            <div className="relative w-28 h-28 sm:w-36 sm:h-36">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 128 128">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="rgba(0, 200, 83, 0.2)"
                  strokeWidth="12"
                  fill="transparent"
                />
                <motion.circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="url(#gradient)"
                  strokeWidth="12"
                  fill="transparent"
                  strokeLinecap="round"
                  initial={{ strokeDasharray: "0 352" }}
                  animate={inView ? { strokeDasharray: "348 352" } : {}}
                  transition={{ duration: 2, delay: 0.5 }}
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00C853" />
                    <stop offset="100%" stopColor="#69F0AE" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.span
                  className="text-2xl sm:text-3xl font-bold text-bb-green-light"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 1 }}
                >
                  99.1%
                </motion.span>
              </div>
            </div>
            {/* Responsive Text */}
            <div className="text-center sm:text-left mt-4 sm:mt-0">
              <p className="text-gray-400 text-lg">
                "This is art, Mr. White."
              </p>
              <p className="text-bb-green-light text-sm mt-2">
                - Jesse Pinkman (reviewing my code)
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
